/**
 * The language switcher.
 *
 * BY NAME, NEVER BY FLAG. A flag is a country, not a language: 🇨🇳 excludes
 * Taiwanese and Singaporean readers of the same script, 🇷🇺 excludes Kazakh and
 * Belarusian Russian speakers, and 🇬🇧/🇺🇸 is a coin toss that annoys half the
 * room. Endonyms — Tiếng Việt / 中文 / Русский / English — are unambiguous and
 * legible to exactly the person who needs them.
 *
 * The list comes from `GET /customer/session.locales`, i.e. what the BRANCH has
 * enabled, never a hard-coded array. Enabling Korean is a database row.
 */

import { useState } from 'react';
import { useCustomerI18n } from './CustomerI18nProvider';

export function LanguageSwitcher(): React.JSX.Element {
  const { locale, locales, setLocale, tOr } = useCustomerI18n();
  const [open, setOpen] = useState(false);
  const current = locales.find((l) => l.locale === locale);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={tOr('customer.language.switch', 'Đổi ngôn ngữ')}
        className="flex min-h-[44px] items-center gap-2 rounded-full bg-surface px-4 py-2 text-sm font-semibold text-fg shadow-card ring-1 ring-line"
      >
        <span aria-hidden="true">🌐</span>
        {/* The endonym, not a flag. */}
        <span className="text-flow">{current?.nameNative ?? locale}</span>
      </button>

      {open ? (
        <ul
          role="listbox"
          aria-label={tOr('customer.language.choose', 'Chọn ngôn ngữ')}
          className="absolute right-0 z-40 mt-2 min-w-[12rem] overflow-hidden rounded-lg bg-surface py-1 shadow-pop ring-1 ring-line"
        >
          {locales.map((option) => (
            <li key={option.locale}>
              <button
                type="button"
                role="option"
                aria-selected={option.locale === locale}
                lang={option.locale}
                onClick={() => {
                  // Switching language NEVER clears the cart: the cart lives in
                  // CustomerApp, above the i18n provider, and nothing in this
                  // path touches it. It also never changes the currency — see
                  // CURRENCY GUARANTEE #1/#2 in CustomerI18nProvider.
                  void setLocale(option.locale);
                  setOpen(false);
                }}
                className={`text-flow flex min-h-[48px] w-full items-center justify-between gap-3 px-4 py-2 text-left text-base ${
                  option.locale === locale ? 'bg-brand-50 font-semibold text-brand-700' : 'text-fg'
                }`}
              >
                <span>{option.nameNative}</span>
                {option.locale === locale ? <span aria-hidden="true">✓</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
